import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, Clock, FileText, DollarSign, Users, 
  Award, ArrowRight, Phone, Globe, Briefcase, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import RecruitmentToStudyAbroadStrip from '@/components/common/RecruitmentToStudyAbroadStrip';
import TrustSignalStrip from '@/components/common/TrustSignalStrip';
import { usePageMeta, useServiceSchema } from '@/hooks/useSEO';

const benefits = [
  'Live and work permanently in Canada',
  'Access to world-class healthcare',
  'Free education for children',
  'Path to Canadian citizenship',
  'Sponsor family members',
  'Travel-free access to many countries',
  'High quality of life and safety',
  'Strong job market and opportunities',
];

const eligibility = [
  { factor: 'Age', details: 'Maximum points for 20-29 years' },
  { factor: 'Education', details: 'Minimum Bachelor\'s degree preferred' },
  { factor: 'Language', details: 'IELTS CLB 7+ recommended' },
  { factor: 'Work Experience', details: '1-3+ years skilled work experience' },
  { factor: 'Adaptability', details: 'Canadian relatives, previous visits' },
];

const processSteps = [
  { step: 1, title: 'Profile Assessment', description: 'Free evaluation of your eligibility and CRS score calculation', duration: '1-2 days' },
  { step: 2, title: 'Document Preparation', description: 'IELTS, ECA, police clearance, and other required documents', duration: '4-8 weeks' },
  { step: 3, title: 'Express Entry Profile', description: 'Create profile in the Express Entry pool', duration: '1-2 days' },
  { step: 4, title: 'Provincial Nomination', description: 'Apply for PNP if required for additional 600 points', duration: '2-6 months' },
  { step: 5, title: 'ITA & Application', description: 'Submit complete PR application after receiving ITA', duration: '60 days' },
  { step: 6, title: 'Medical & Biometrics', description: 'Complete medical examination and biometrics', duration: '2-4 weeks' },
  { step: 7, title: 'COPR & Landing', description: 'Receive Confirmation of PR and land in Canada', duration: '4-8 months' },
];

const documents = [
  'Valid Passport',
  'IELTS/CELPIP Score Report',
  'Educational Credential Assessment (ECA)',
  'Work Experience Letters',
  'Police Clearance Certificate',
  'Medical Examination Report',
  'Proof of Funds',
  'Passport-size Photographs',
  'Marriage Certificate (if applicable)',
  'Birth Certificate',
];

const programs = [
  { name: 'Federal Skilled Worker Program (FSWP)', description: 'For skilled workers with foreign work experience' },
  { name: 'Canadian Experience Class (CEC)', description: 'For those with Canadian work experience' },
  { name: 'Federal Skilled Trades Program (FSTP)', description: 'For qualified tradespeople' },
  { name: 'Provincial Nominee Program (PNP)', description: 'Province-specific nomination with 600 bonus points' },
];

const CanadaPRPage = () => {
  // SEO: unique title & meta description for /countries/canada/canada-pr-visa (new canonical URL)
  usePageMeta(
    'Canada PR Visa 2026: Express Entry, PNP & Skilled Migration',
    'Apply for Canada PR Visa in 2026 — Express Entry, PNP & skilled migration pathways. Eligibility, CRS, documents, fees & timelines explained.',
    { canonical: '/countries/canada/canada-pr-visa' },
  );
  useServiceSchema({
    name: 'Canada PR Visa Consulting (Express Entry & PNP)',
    description:
      'End-to-end Canada Permanent Residency consulting — Express Entry profile creation, CRS score optimisation, Provincial Nominee Program (PNP) applications, ECA and IELTS support.',
    serviceType: 'Immigration Consulting',
    areaServed: ['Canada'],
    url: 'https://zafco.in/countries/canada/canada-pr-visa',
  });

  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20Canada%20PR%20recruitment', '_blank');
  };

  return (
    <div data-testid="canada-pr-page">
      <PageHeader 
        title="Canada PR Visa 2026: Express Entry, PNP & Skilled Migration"
        subtitle="Expert guidance for Canada Express Entry, Provincial Nominee Programs (PNP), Federal Skilled Worker Program and skilled migration — trusted MEA-approved recruitment partner."
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'Canada', path: '#' },
          { label: 'Canada PR' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1618229234536-dd459d33ad73?w=1920"
        keywordsTier={['MEA Approved', 'Express Entry', 'Provincial Nominee Program']}
      />

      {/* E-E-A-T trust signals — MEA license, placements, success rate */}
      <TrustSignalStrip />

      {/* Introduction */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                Overseas Jobs in Canada Leading to PR
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                Canada Express Entry Recruitment 2026
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Canada remains the top destination for skilled Indian professionals seeking overseas employment 
                and permanent residency. The Express Entry system offers one of the fastest pathways to PR, 
                with processing times as quick as 6 months.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                At Zafco Human Resource Management, we specialize in connecting Indian talent with Canadian 
                opportunities through Federal Skilled Worker Program, Canadian Experience Class, Provincial 
                Nominee Programs, and other immigration pathways.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Clock, label: 'Processing Time', value: '6-12 Months' },
                  { icon: DollarSign, label: 'Min Investment', value: 'CAD 13,757' },
                  { icon: Users, label: 'Success Rate', value: '98%' },
                  { icon: Award, label: 'Programs', value: '4+' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl p-4">
                    <stat.icon className="w-6 h-6 text-zafco-teal mb-2" />
                    <p className="text-2xl font-bold text-zafco-blue">{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Calculate Your CRS Score
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free Canada PR Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              Benefits of Canada PR
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Permanent residency in Canada opens doors to countless opportunities for you and your family.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              Canada Immigration Programs
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Multiple pathways to achieve your Canadian dream based on your profile and preferences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.name}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-zafco-blue to-zafco-teal flex items-center justify-center mb-4">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-2">{program.name}</h3>
                <p className="text-gray-600">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-8">
                Eligibility Requirements
              </h2>
              <div className="space-y-4">
                {eligibility.map((item) => (
                  <div key={item.factor} className="bg-white rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-zafco-blue/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-zafco-blue" />
                    </div>
                    <div>
                      <h4 className="font-bold text-zafco-blue">{item.factor}</h4>
                      <p className="text-gray-600 text-sm">{item.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-8">
                Required Documents
              </h2>
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <ul className="space-y-3">
                  {documents.map((doc) => (
                    <li key={doc} className="flex items-center gap-3 text-gray-700">
                      <FileText className="w-5 h-5 text-zafco-teal" />
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              Canada PR Process with Zafco
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our streamlined process ensures a smooth journey from assessment to landing in Canada.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                className="flex gap-6 mb-8 last:mb-0"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-zafco-blue to-zafco-teal flex items-center justify-center text-white font-bold">
                    {step.step}
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gradient-to-b from-zafco-blue to-zafco-teal mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-zafco-blue">{step.title}</h3>
                      <span className="text-sm text-zafco-teal font-medium">{step.duration}</span>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue via-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Start Your Canada PR Journey?
          </motion.h2>
          <motion.p 
            className="text-xl text-white/80 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Get your free CRS score calculation and personalized immigration pathway assessment today.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button 
              onClick={openWhatsApp}
              size="lg"
              className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
            >
              <Phone className="w-6 h-6 mr-3" />
              Get Free Assessment
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Internal cross-link → Study Abroad cluster (SEO topical adjacency) */}
      <RecruitmentToStudyAbroadStrip
        country="Canada"
        studyHref="/study-abroad/canada/"
        visaHref="/study-abroad/free-profile-evaluation/?source=canada-pr-cross-link"
        visaLabel="Free Eligibility Check"
      />
    </div>
  );
};

export default CanadaPRPage;
